import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasRole, primaryRole } from '../utils/roles';

/**
 * Wraps a route element to enforce:
 *   1. The user must be logged in.
 *   2. (Optional) The user's role must match one of the `allowedRoles`.
 *
 * Usage:
 *   <ProtectedRoute allowedRoles={['admin']}>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 */
export default function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Don't render anything until the auth state has been hydrated from
  // localStorage — prevents a flash redirect on page refresh.
  if (loading) {
    return <div className="loading-auth">Loading…</div>;
  }

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Checks the full role set, not the single legacy `role` string. A judge who
  // registered as a contestant first still has users.role = 'contestant', and
  // this test used to bounce them off the judge dashboard silently — no error,
  // just an unexplained trip back to their own entry page.
  if (allowedRoles.length > 0 && !hasRole(user, allowedRoles)) {
    // Redirect to the user's own dashboard rather than a generic 403 page.
    const defaultPaths = {
      contestant: '/contestant/dashboard',
      judge: '/judge/dashboard',
      admin: '/admin/dashboard',
      observer: '/viewer/results',
    };
    return <Navigate to={defaultPaths[primaryRole(user)] ?? '/login'} replace />;
  }

  return children;
}
